import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_error, show_success, remove_flash, check_auth_status } from "lib/helpers"

// jobs--import
//
// Manages the Custom Objects import page: DataTable for import jobs,
// file upload forms (ajaxForm), status polling, and bulk removal.
export default class extends Controller {
  static targets = ["table", "classSelect", "importForm", "schemaForm", "checkAll"]

  static values = {
    getImportPath: String,
    checkStatusPath: String,
    removeImportsPath: String,
    customClassesPath: String,
    isReader: Boolean,
  }

  connect() {
    this.#initDataTable()
    this.#initAjaxForms()
    this.element.addEventListener("change", this.boundRowCheckboxChanged)
    this.#statusCheckOnce()
  }

  disconnect() {
    clearTimeout(this.statusTimer)
    this.statusTimer = null

    this.element.removeEventListener("change", this.boundRowCheckboxChanged)

    if (this.hasImportFormTarget) $(this.importFormTarget).ajaxFormUnbind()
    if (this.hasSchemaFormTarget) $(this.schemaFormTarget).ajaxFormUnbind()

    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
  }

  classChanged() {
    const importPath = `${this.customClassesPathValue}/import?class_id=${this.classSelectTarget.value}`
    this.importFormTarget.setAttribute("action", importPath)
  }

  fileDataChanged(event) {
    if (event.target.files[0]) this.#submitForm(this.importFormTarget)
  }

  fileSchemaChanged(event) {
    if (event.target.files[0]) this.#submitForm(this.schemaFormTarget)
  }

  toggleAll() {
    const isChecked = this.checkAllTarget.checked
    $(this.tableTarget).closest(".dataTables_wrapper")
      .find(".table-checkbox-costum-import")
      .prop("checked", isChecked)
      .closest("tr")
      .toggleClass("selected-row", isChecked)
  }

  removeSelected(event) {
    event.preventDefault()

    const $wrapper = $(this.tableTarget).closest(".dataTables_wrapper")
    const $checked = $wrapper.find(".table-checkbox-costum-import:checked")
    const $all = $wrapper.find(".table-checkbox-costum-import")

    if (!$all.length) return
    if (!$checked.length) { alert("Please select at least one item"); return }
    if (!confirm("Are you sure? All selected items will be removed")) return

    const allSelected = $checked.length === $all.length
    const ids = $checked.map(function () { return $(this).val() }).get()

    $wrapper.find(".dataTables_processing").css("display", "block")

    $.ajax({
      url: this.removeImportsPathValue,
      type: "DELETE",
      data: { item_ids: ids },
      success: () => {
        if (allSelected) {
          const settings = this.dataTable.settings()[0]
          if (settings._iRecordsDisplay - settings._iDisplayStart <= settings._iDisplayLength) {
            this.dataTable.page("previous")
          }
        }
        this.dataTable.draw(false)
      },
    })

    if (allSelected) this.checkAllTarget.click()
  }

  // -- class field arrow handlers for add/removeEventListener --

  boundRowCheckboxChanged = (event) => {
    if (!event.target.matches(".table-checkbox-costum-import")) return

    const row = event.target.closest("tr")
    if (event.target.checked) {
      row.classList.add("selected-row")
    } else {
      row.classList.remove("selected-row")
    }

    const $wrapper = $(this.tableTarget).closest(".dataTables_wrapper")
    const selectedCount = $wrapper.find(".table-checkbox-costum-import:checked").length
    const totalCount = $wrapper.find(".table-checkbox-costum-import").length
    this.checkAllTarget.checked = selectedCount > 0 && selectedCount === totalCount
  }

  // -- private --

  #initDataTable() {
    this.dataTable = createTurboDataTable(this.tableTarget, {
      dom: 'l<"jobs_label">rtip<"toolbar">',
      scrollX: true,
      pagingType: "full_numbers",
      processing: true,
      serverSide: true,
      order: [7, "desc"],
      ajax: this.getImportPathValue,
      columnDefs: [
        {
          targets: 0,
          createdCell(cell, cellData) {
            $(cell).html(
              `<input id="check--dialogs ${cellData}" class="table-checkbox table-checkbox-costum-import" type="checkbox" name="item_ids[]" value="${cellData}"/>` +
              ` <label for="check--dialogs ${cellData}"></label>`,
            )
          },
        },
        {
          targets: [-1],
          createdCell(cell, cellData) {
            $(cell).html(cellData ? moment(cellData).format() : "")
          },
        },
        { sortable: false, targets: [0, 1, 3, 4, 5] },
      ],
    })

    if (!this.isReaderValue) {
      const $wrapper = $(this.tableTarget).closest(".dataTables_wrapper")
      $wrapper.find("div.toolbar").html(
        '<br/><br/><a href="#" class="remove" data-action="click->jobs--import#removeSelected">Remove selected</a>',
      )
    }
  }

  #submitForm(formEl) {
    // Native form.submit() skips the submit event, so ajaxForm never runs and
    // the hidden iframe swallows the JSON response without refreshing the table.
    $(formEl).ajaxSubmit(this.#ajaxFormOptions(formEl))
  }

  #ajaxFormOptions(formEl) {
    const self = this

    return {
      dataType: "json",
      resetForm: true,
      beforeSubmit() { remove_flash() },
      uploadProgress(_event, _position, _total, percentComplete) {
        $(formEl).parents("#tab-custom-import").find(".bar").width(`${percentComplete}%`)
      },
      success(response) {
        if (response.success) {
          show_success(response.message, $(formEl).parents("#workspace"))
          self.#updateClassesSelect(response.data)
        } else {
          show_error(response.message, $(formEl).parents("#workspace"))
        }
        self.dataTable.ajax.reload()
        self.dataTable.draw(false)
        self.#statusCheckOnce()
      },
      error(response) {
        show_error("An error occurred during request", $(formEl).parents("#workspace"))
        self.#statusCheckOnce()
        check_auth_status(response.status)
      },
    }
  }

  #initAjaxForms() {
    ;[this.importFormTarget, this.schemaFormTarget].forEach((formEl) => {
      $(formEl).ajaxForm(this.#ajaxFormOptions(formEl))
    })
  }

  #updateClassesSelect(data) {
    if (!Array.isArray(data)) return
    const select = this.classSelectTarget
    data.forEach((item) => {
      select.appendChild(new Option(item.name, item._id))
    })
  }

  #statusCheckOnce() {
    clearTimeout(this.statusTimer)
    this.statusTimer = setTimeout(() => this.#checkStatus(), 5000)
  }

  #checkStatus() {
    $.ajax({
      url: this.checkStatusPathValue,
      dataType: "JSON",
      success: (response) => {
        if (response.total_job > 0 || response.status) {
          this.dataTable.ajax.reload()
          this.dataTable.draw(false)
        }
        if (!response.status) this.#statusCheckOnce()
      },
    })
  }
}
